import { useState, useEffect, useCallback, useRef } from "react";
import { X } from "lucide-react";

interface HighProbabilityAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockName: string;
  selectedDate: Date | null;
  isFilteredOut?: boolean;
  /** SPA navigation to analyze-stock (sets session intent + client-side route) */
  onNavigateToAnalyzeStock?: (symbol: string) => void;
}

export default function HighProbabilityAlertModal({
  isOpen,
  onClose,
  stockName,
  isFilteredOut,
  onNavigateToAnalyzeStock,
}: HighProbabilityAlertModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  const redirectToAnalysePage = (e: React.MouseEvent) => {
    e.stopPropagation();
    startCloseSequence();

    if (onNavigateToAnalyzeStock) {
      onNavigateToAnalyzeStock(stockName);
      return;
    }
    sessionStorage.setItem(
      "lastSearchCompany",
      JSON.stringify({ symbol: stockName }),
    );
    window.location.assign("/analyze-stock");
  };

  const startCloseSequence = useCallback(() => {
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      onClose();
      closeTimerRef.current = null;
    }, 400);
  }, [onClose]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isOpen) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      // Slight delay ensures the DOM is ready for the CSS transition and prevents synchronous setState lint errors
      setTimeout(() => setIsVisible(true), 10);
    } else {
      startCloseSequence();
    }

    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isOpen, startCloseSequence]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  if (!isOpen && !isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center backdrop-blur-sm bg-black/40"
      // onClick={startCloseSequence}
    >
      <div
        style={{
          transition: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
        }}
        className="relative z-[9999] w-full max-w-lg cursor-pointer"
        // onClick={startCloseSequence}
      >
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-4 border border-gray-100 dark:border-gray-800">
          <button
            onClick={(e) => {
              e.stopPropagation();
              startCloseSequence();
            }}
            className="cursor-pointer absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="text-gray-900 dark:text-gray-100">
            {isFilteredOut ? (
              <p>
                <span className="font-semibold text-red-600 dark:text-red-400">
                  {stockName}
                </span>{" "}
                does not fit your filtered criteria, try reducing/removing
                applied filters.
              </p>
            ) : (
              <div>
                <p className="text-base">
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {stockName}
                  </span>{" "}
                  does not match ABillion&apos;s High Probability criteria.
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Try using the{" "}
                  <span
                    className="cursor-pointer font-medium text-blue-600 underline transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={redirectToAnalysePage}
                  >
                    Analyze your stock
                  </span>{" "}
                  module for a deep dive analysis.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
